import { type KeyboardEvent, type ReactNode, useEffect, useId, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type BannerResponse, type Locale, pickLocalized } from '@aps/contracts';
import { Banner, Button, useToast } from '@aps/ui';
import { ApiError, api } from '../lib/api.js';
import { messages } from '../i18n/messages.js';
import { useSession } from '../app/session-context.js';
import {
  BannerImageField,
  NO_IMAGE_CHANGE,
  useBannerImagePreview,
  type BannerImageChoice,
} from './BannerImageControl.js';
import { BannerLinkField } from './BannerLinkField.js';
import {
  EMPTY_BANNER_DRAFT,
  bannerCreateDto,
  bannerDraft,
  bannerProblems,
  bannerTextFromProduct,
  bannerUpdateDto,
  type BannerDraft,
} from './banner-draft.js';

/**
 * Banner yaratish/tahrirlash oynasi.
 *
 * Bitta ekranda: tepada mijoz ilovasidagi jonli ko'rinish, pastda to'rt qadam (rasm → matn →
 * havola → muddat). Rasm banner hali yaratilmagan bo'lsa ham tanlanadi — serverga hammasi
 * "Saqlash" bilan birga ketadi, shuning uchun "avval yarating, keyin rasm qo'ying" kerak emas.
 * Xato tugma yonida chiqadi; yopishda saqlanmagan o'zgarish bo'lsa so'raladi.
 */
export function BannerEditor({
  banner,
  onClose,
}: {
  /** `null` — yangi banner. */
  banner: BannerResponse | null;
  onClose: () => void;
}): ReactNode {
  const { locale } = useSession();
  const t = messages(locale);
  const client = useQueryClient();
  const toast = useToast();
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);

  const initial = banner === null ? EMPTY_BANNER_DRAFT : bannerDraft(banner);
  const [draft, setDraft] = useState<BannerDraft>(initial);
  const [choice, setChoice] = useState<BannerImageChoice>(NO_IMAGE_CHANGE);
  const [error, setError] = useState<string | null>(null);
  const [confirmLeave, setConfirmLeave] = useState(false);
  const [previewLocale, setPreviewLocale] = useState<Locale>(locale);

  const set = <K extends keyof BannerDraft>(field: K, value: BannerDraft[K]): void => {
    setDraft((current) => ({ ...current, [field]: value }));
    setError(null);
  };

  const previewUrl = useBannerImagePreview(banner?.imageUrl ?? null, choice);
  const problems = bannerProblems(draft, banner === null);
  const dto = banner === null ? null : bannerUpdateDto(draft, banner);
  const imageChanged = choice.file !== null || choice.removed;
  const dirty = JSON.stringify(draft) !== JSON.stringify(initial) || imageChanged;
  const hasChanges =
    banner === null || (dto !== null && Object.keys(dto).length > 0) || imageChanged;
  const invalid = problems.title || problems.link || problems.dates || problems.sort;

  const blocker = problems.title
    ? t.refBannerNeedTitle
    : problems.link
      ? t.refBannerLinkIncomplete
      : problems.dates
        ? t.refBannerDateInvalid
        : problems.sort
          ? t.refBannerSortInvalid
          : null;

  const save = useMutation({
    mutationFn: async (): Promise<{ imageFailed: boolean }> => {
      let id: string;
      if (banner === null) {
        id = (await api.createBanner(bannerCreateDto(draft))).id;
      } else {
        if (dto !== null && Object.keys(dto).length > 0) await api.updateBanner(banner.id, dto);
        id = banner.id;
      }
      // Matn saqlandi; rasm yiqilsa banner yo'qolmaydi — foydalanuvchiga aytiladi.
      try {
        if (choice.file !== null) await api.uploadBannerImage(id, choice.file);
        else if (choice.removed) await api.deleteBannerImage(id);
      } catch {
        return { imageFailed: true };
      }
      return { imageFailed: false };
    },
    onSuccess: ({ imageFailed }) => {
      void client.invalidateQueries({ queryKey: ['banners'] });
      if (imageFailed) toast.show(t.refBannerImageFailedAfterSave, 'danger');
      else toast.show(banner === null ? t.toastAdded : t.toastSaved, 'success');
      onClose();
    },
    onError: (cause) => {
      setError(cause instanceof ApiError ? cause.text(locale) : t.errorTitle);
    },
  });

  const busy = save.isPending;
  const canSave = !invalid && hasChanges && !busy;

  const requestClose = (): void => {
    if (busy) return;
    if (dirty) setConfirmLeave(true);
    else onClose();
  };
  const closeRef = useRef(requestClose);
  useEffect(() => {
    closeRef.current = requestClose;
  });

  // Oyna ochiq: sahifa siljimaydi, fokus oynaga o'tadi va yopilganda qaytadi.
  useEffect(() => {
    const previous = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    panelRef.current?.focus();
    return () => {
      document.body.style.overflow = previousOverflow;
      if (previous instanceof HTMLElement) previous.focus();
    };
  }, []);

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (event.key !== 'Escape') return;
    // Ichki kesish oynasining Escape'i faqat o'zini yopadi.
    if (event.target instanceof Element && event.target.closest('.adm-crop') !== null) return;
    closeRef.current();
  };

  const text = (uz: string, ru: string): string =>
    pickLocalized(uz.trim(), ru.trim() === '' ? null : ru.trim(), previewLocale);
  const previewTitle = text(draft.titleUz, draft.titleRu) || t.refBannerPreviewTitle;
  const previewSubtitle = text(draft.subtitleUz, draft.subtitleRu);
  const previewBadge = text(draft.badgeUz, draft.badgeRu);

  const field = (
    name: 'titleUz' | 'titleRu' | 'subtitleUz' | 'subtitleRu' | 'badgeUz' | 'badgeRu',
    label: string,
  ): ReactNode => (
    <label className="aps-field" key={name}>
      <span className="aps-field__label">{label}</span>
      <input
        className="aps-field__input"
        maxLength={name.startsWith('title') ? 120 : name.startsWith('badge') ? 40 : 200}
        value={draft[name]}
        onChange={(event) => {
          set(name, event.target.value);
        }}
      />
    </label>
  );

  return (
    <div className="adm-modal">
      <div className="adm-modal__backdrop" aria-hidden="true" />
      <div
        ref={panelRef}
        className="adm-modal__panel adm-banner-editor"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        onKeyDown={onKeyDown}
      >
        <header className="adm-banner-editor__header">
          <h2 id={titleId} className="adm-banner-editor__title">
            {banner === null ? t.refAddBanner : t.refEditBanner}
          </h2>
          <Button
            size="sm"
            variant="ghost"
            aria-label={t.refBannerEditorClose}
            disabled={busy}
            onClick={requestClose}
          >
            ✕
          </Button>
        </header>

        <div className="adm-banner-editor__body">
          <section className="adm-banner-preview" aria-label={t.refBannerPreview}>
            <div className="adm-banner-preview__bar">
              <span className="aps-muted">{t.refBannerPreview}</span>
              <span className="adm-banner-preview__lang" role="group" aria-label="UZ / RU">
                {(['UZ', 'RU'] as const).map((code) => (
                  <Button
                    key={code}
                    size="sm"
                    variant={previewLocale === code ? 'primary' : 'ghost'}
                    aria-pressed={previewLocale === code}
                    onClick={() => {
                      setPreviewLocale(code);
                    }}
                  >
                    {code}
                  </Button>
                ))}
              </span>
            </div>
            <div className="aps-banners adm-banner-preview__phone">
              <Banner
                title={previewTitle}
                {...(previewSubtitle === '' ? {} : { subtitle: previewSubtitle })}
                {...(previewBadge === '' ? {} : { badge: previewBadge })}
                {...(draft.colorKey === '' ? {} : { categoryKey: draft.colorKey })}
                {...(previewUrl === null ? {} : { imageUrl: previewUrl })}
              />
            </div>
          </section>

          <section className="adm-banner-step">
            <h3 className="adm-banner-step__title">
              <span className="adm-banner-step__num" aria-hidden="true">
                1
              </span>
              {t.refBannerStep1}
            </h3>
            <p className="aps-muted">{t.refBannerStep1Hint}</p>
            <BannerImageField
              previewUrl={previewUrl}
              choice={choice}
              disabled={busy}
              onChange={(next) => {
                // Rasmi yo'q bannerda "olib tashlash" faqat yangi tanlovni bekor qiladi.
                setChoice({
                  file: next.file,
                  removed: next.removed && banner?.imageUrl != null,
                });
                setError(null);
              }}
            />
            {previewUrl === null && (
              <label className="aps-field">
                <span className="aps-field__label">{t.refBannerColor}</span>
                <select
                  className="aps-field__input"
                  value={draft.colorKey}
                  onChange={(event) => {
                    set('colorKey', event.target.value);
                  }}
                >
                  <option value="">{t.refBannerColorNone}</option>
                  {(
                    [
                      ['engine', t.refBannerColorEngine],
                      ['brakes', t.refBannerColorBrakes],
                      ['oils', t.refBannerColorOils],
                      ['electric', t.refBannerColorElectric],
                      ['tires', t.refBannerColorTires],
                      ['access', t.refBannerColorAccess],
                    ] as const
                  ).map(([key, label]) => (
                    <option key={key} value={key}>
                      {label}
                    </option>
                  ))}
                </select>
              </label>
            )}
          </section>

          <section className="adm-banner-step">
            <h3 className="adm-banner-step__title">
              <span className="adm-banner-step__num" aria-hidden="true">
                2
              </span>
              {t.refBannerStep2}
            </h3>
            <p className="aps-muted">{t.refBannerStep2Hint}</p>
            <div className="adm-banner-form">
              {field('titleUz', t.refBannerTitleUz)}
              {field('titleRu', t.refBannerTitleRu)}
              {field('subtitleUz', t.refBannerSubtitleUz)}
              {field('subtitleRu', t.refBannerSubtitleRu)}
              {field('badgeUz', t.refBannerBadgeUz)}
              {field('badgeRu', t.refBannerBadgeRu)}
            </div>
          </section>

          <section className="adm-banner-step">
            <h3 className="adm-banner-step__title">
              <span className="adm-banner-step__num" aria-hidden="true">
                3
              </span>
              {t.refBannerStep3}
            </h3>
            <p className="aps-muted">{t.refBannerStep3Hint}</p>
            <BannerLinkField
              value={{ linkType: draft.linkType, linkTarget: draft.linkTarget }}
              onChange={(next) => {
                setDraft((current) => ({ ...current, ...next }));
                setError(null);
              }}
              onFillFromProduct={(product) => {
                setDraft((current) => ({ ...current, ...bannerTextFromProduct(product) }));
                setError(null);
              }}
            />
            {problems.link && <p className="aps-error">{t.refBannerLinkIncomplete}</p>}
          </section>

          <section className="adm-banner-step">
            <h3 className="adm-banner-step__title">
              <span className="adm-banner-step__num" aria-hidden="true">
                4
              </span>
              {t.refBannerStep4}
            </h3>
            <p className="aps-muted">{t.refBannerStep4Hint}</p>
            <div className="adm-banner-form">
              {(['startsAt', 'endsAt'] as const).map((name) => (
                <label className="aps-field" key={name}>
                  <span className="aps-field__label">
                    {name === 'startsAt' ? t.refBannerStartsAt : t.refBannerEndsAt}
                  </span>
                  <input
                    className="aps-field__input"
                    type="datetime-local"
                    value={draft[name]}
                    onChange={(event) => {
                      set(name, event.target.value);
                    }}
                  />
                </label>
              ))}
              <label className="aps-field">
                <span className="aps-field__label">{t.refBannerSort}</span>
                <input
                  className="aps-field__input"
                  type="number"
                  min="0"
                  max="9999"
                  step="1"
                  value={draft.sort}
                  onChange={(event) => {
                    set('sort', event.target.value);
                  }}
                />
              </label>
            </div>
            {problems.dates && <p className="aps-error">{t.refBannerDateInvalid}</p>}
            {problems.sort && <p className="aps-error">{t.refBannerSortInvalid}</p>}
          </section>
        </div>

        <footer className="adm-banner-editor__footer">
          {error !== null && (
            <p className="aps-error" role="alert">
              {error}
            </p>
          )}
          {confirmLeave ? (
            <div className="adm-banner-editor__actions" role="alert">
              <span>{t.refBannerDiscardTitle}</span>
              <Button size="sm" variant="danger" onClick={onClose}>
                {t.refBannerDiscard}
              </Button>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => {
                  setConfirmLeave(false);
                }}
              >
                {t.refBannerKeepEditing}
              </Button>
            </div>
          ) : (
            <div className="adm-banner-editor__actions">
              {blocker !== null && <span className="aps-muted">{blocker}</span>}
              <Button variant="ghost" disabled={busy} onClick={requestClose}>
                {t.cancel}
              </Button>
              <Button
                variant={canSave ? 'primary' : 'secondary'}
                disabled={!canSave}
                onClick={() => {
                  save.mutate();
                }}
              >
                {banner === null ? t.refAdd : t.refSaveBanner}
              </Button>
            </div>
          )}
        </footer>
      </div>
    </div>
  );
}
